import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getOpportunityAccounts, getOpportunityUsers } from "../data/opportunities/opportunitiesSlice";
import { fetchOpportunityById } from "../features/opportunities/data/opportunitiesService";
import { formatNumber } from "../utils/numberUtils";
import { fetchQuoteTemplateByFilter } from "../features/quoteTemplate/data/quoteTemplateService";
import { useParams } from "react-router-dom";
import { fetchSingleAccount, fetchSingleOpportunity } from "../features/opportunities/data/opportunitiesService";

/**
 * useQuoteRequestData
 *
 * Central data hook that derives everything the quote preview and export pipeline
 * needs from the current quote revision, its items, and the selected template.
 *
 * It is responsible for:
 *  - Flattening upfront/recurring groups and items into ordered, display-ready lists
 *    (`upfrontItems`, `recurringItems`).
 *  - Building the export payload (`exportData`) — hidden items removed, fields
 *    formatted, groups collapsed onto their first child.
 *  - Assembling `customContent` — the merged list of saved field values
 *    (`quoteAdditionalFields`), auto-calculated values (totals, VAT, dates, etc.),
 *    and Salesforce-defaulted values — keyed by the currently selected template's
 *    `customSections` definition.
 *  - Fetching supporting data (Salesforce opportunity, account, all templates) via
 *    React Query, exposing loading/error state for the caller.
 *
 * ─── Parameters ────────────────────────────────────────────────────────────────
 *
 * @param {object}      quoteRevision          - The full quote revision object as
 *   returned by the API (parsed). Must include at minimum:
 *     • `quoteTemplateId`       — used as fallback template lookup on initial load.
 *     • `quoteAdditionalFields` — array of { id, field, value } saved by QuoteContent;
 *       used as the authoritative source of user-entered custom field values.
 *     • `quoteCustomisation`    — groups/items structure for the line editor.
 *     • `revisionNumber`, `quoteId` — used in calculated custom fields.
 *
 * @param {Array}       quoteItems             - Flat array of all quote line items for
 *   this revision. Used to populate group children and compute price totals.
 *
 * @param {object|null} liveQuoteCustomisation - An optional *live* (unsaved) override
 *   for the customisation structure. When provided it takes priority over
 *   `quoteRevision.quoteCustomisation`. Pass `null` (or omit) to use the server value.
 *   Typically provided by ViewQuote when the user is actively reordering groups/items
 *   before the next autosave completes.
 *
 * @param {object|null} selectedTemplate       - The currently selected template object
 *   (as returned by the templates query — same shape as entries in `templatesData`).
 *   This is intentionally a local-state value rather than deriving it from
 *   `quoteRevision.quoteTemplateId`, because `quoteTemplateId` only updates *after* a
 *   save round-trip completes. Passing `selectedTemplate` directly keeps `customContent`
 *   in sync with the UI immediately when the user switches templates, avoiding a window
 *   where the preview renders new-template layout with old-template field values.
 *   Defaults to `null`; the hook falls back to a `templatesData` lookup by
 *   `quoteRevision.quoteTemplateId` when null (e.g. initial page load before
 *   QuoteEditor has populated its local state).
 *
 * ─── Returns ───────────────────────────────────────────────────────────────────
 *
 * @returns {{
 *   salesForceData:        { opportunity, accounts, users },
 *   customContent:         Array<{ id, field, value }>,
 *   exportData:            { upfront: Array, recurring: Array },
 *   upfrontItems:          Array,
 *   recurringItems:        Array,
 *   isLoadingOpportunity:  boolean,
 *   isLoadingTemplates:    boolean,
 *   templatesData:         Array,
 *   templatesError:        Error|null,
 * }}
 *
 * ─── Usage ─────────────────────────────────────────────────────────────────────
 *
 * // In a component that owns selectedTemplate state:
 * const [selectedTemplate, setSelectedTemplate] = useState(null);
 *
 * const {
 *   customContent,
 *   exportData,
 *   upfrontItems,
 *   recurringItems,
 *   isLoadingTemplates,
 *   templatesData,
 * } = useQuoteRequestData(quoteRevision, quoteItems, quoteCustomisation, selectedTemplate);
 *
 * // Pass selectedTemplate and setSelectedTemplate down to QuoteContent so it can
 * // update the local state synchronously when the user picks a template.  The hook
 * // will immediately recompute customContent for the new template without waiting for
 * // the save round-trip to update quoteRevision.quoteTemplateId.
 */
export const useQuoteRequestData = (quoteRevision, quoteItems, liveQuoteCustomisation, selectedTemplate = null) => {
    const { id } = useParams();

    const [opportunity, setOpportunity] = useState(null);
    const [customContent, setCustomContent] = useState([]);

    const [upfrontItems, setUpfrontItems] = useState([])
    const [recurringItems, setRecurringItems] = useState([])
    const [exportData, setExportData] = useState([])

    const accountId = opportunity?.Account?.Id ?? null;
    const ownerId = opportunity?.Owner?.Id ?? null;

    const allAccounts = useSelector(getOpportunityAccounts);
    const allUsers = useSelector(getOpportunityUsers);
    const quoteCustomisation = useMemo(() => {
        const qc = liveQuoteCustomisation ?? quoteRevision?.quoteCustomisation ?? {};
        return {
            ...qc,
            groups: {
                upfront: Array.isArray(qc?.groups?.upfront) ? qc.groups.upfront : [],
                recurring: Array.isArray(qc?.groups?.recurring) ? qc.groups.recurring : [],
            },
        };
    }, [liveQuoteCustomisation, quoteRevision?.quoteCustomisation]);

    const getOpportunity = async () => {
        const opportunity = await fetchOpportunityById(quoteRevision?.quote?.salesforceOpportunityId);
        return opportunity?.records;
    }

    const {
        data: opportunityData,
        isLoading: isLoadingOpportunity
    } = useQuery({
        queryKey: ["opportunityData", quoteRevision?.quote?.salesforceOpportunityId],
        queryFn: getOpportunity,
        enabled: !!quoteRevision?.quote?.salesforceOpportunityId,
        staleTime: 5 * 60_000, // 5 minutes
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (opportunityData) {
            setOpportunity(opportunityData);
        }
    }, [opportunityData]);

    const { data: opportunityFull } = useQuery({
        queryKey: ["opportunitySingle", id],
        queryFn: () => fetchSingleOpportunity(id),
        staleTime: 60000,
    });

    const { data: accountFull } = useQuery({
        queryKey: ["accountSingle", id],
        queryFn: () => fetchSingleAccount(id),
        staleTime: 60000,
    });    

    const {
        data: templatesData = [],
        isLoading: isLoadingTemplates,
        error: templatesError,
    } = useQuery({
        queryKey: [
            "templatesFilter",
            { pageNumber: 1, pageSize: 1000, sortKey: "name", sortDir: "ascending" },
        ],
        queryFn: async () => {
            const res = await fetchQuoteTemplateByFilter({
                pageNumber: 1,
                pageSize: 1000,
                sortOptions: { key: "name", direction: "ascending" },
            });
            return Array.isArray(res?.quoteTemplateItems) ? res.quoteTemplateItems : [];
        },
        enabled: !!quoteRevision, // wait for quoteRevisionData to be available
        staleTime: 60_000,
    });    


    //temporary totals
    const toNumber = (v) => {
        if (typeof v === "number") return Number.isFinite(v) ? v : 0;
        if (typeof v === "string") {
        const n = parseFloat(v.replace(/[^0-9.-]/g, ""));
        return Number.isFinite(n) ? n : 0;
        }
        return 0;
    };

    //temporarily totals ----
    const recTotal = quoteItems.reduce((sum, it) => sum + toNumber(it.totalRecurringPrice), 0);
    const upfTotal = quoteItems.reduce((sum, it) => sum + toNumber(it.totalUpfrontPrice), 0);
    const annualTotal = quoteItems.reduce((sum, it) => sum + toNumber(it.totalAnnualPrice), 0);

    const termLengths = new Set(
        quoteItems.filter(x => 
        x.recurringNumPayments &&
        x.recurringIntervalMonths &&
        x.recurringStartMonth
        ).map(item =>
            (item.recurringNumPayments * item.recurringIntervalMonths) + (item.recurringStartMonth - 1)
        )
    );  

    const VAT_RATE = 0.20;

    const recVAT = recTotal * VAT_RATE;
    const recTotalVAT = recTotal + recVAT;

    const annualVAT = annualTotal * VAT_RATE
    const annualTotalVAT = annualTotal + annualVAT

    const upfVAT = upfTotal * VAT_RATE;
    const upfTotalVAT = upfTotal + upfVAT;
    //temporarily totals end ----


    const calculatedCustomContent = useMemo(() => [        
        {
          id: "ax_1124444333221",
          field: "CUSTOM_TODAY",
          value: new Date().toLocaleDateString("en-GB"),
        },
        {
          field: "CUSTOM_REC_TOTAL",
          id: "ax_rec_total",
          value: formatNumber(recTotal,2)
        },
        {
          field: "CUSTOM_ANNUAL_TOTAL",
          id: "ax_annual_total",
          value: formatNumber(annualTotal,2)
        },
        {
          field: "CUSTOM_REC_VAT",
          id: "ax_rec_vat",
          value: formatNumber(recVAT,2),
        },
        {
          field: "CUSTOM_ANNUAL_VAT",
          id: "ax_annual_vat",
          value: formatNumber(annualVAT,2),
        },
        {
          field: "CUSTOM_REC_TOTAL_VAT",
          id: "ax_rec_total_vat",
          value: formatNumber(recTotalVAT,2),
        },
        {
          field: "CUSTOM_ANNUAL_TOTAL_VAT",
          id: "ax_annual_total_vat",
          value: formatNumber(annualTotalVAT,2),
        },
        {
          field: "CUSTOM_UPF_TOTAL",
          id: "ax_upf_total",
          value: formatNumber(upfTotal,2),
        },
        {
          field: "CUSTOM_UPF_VAT",
          id: "ax_upf_vat",
          value: formatNumber(upfVAT,2),
        },
        {
          field: "CUSTOM_UPF_TOTAL_VAT",
          id: "ax_upf_total_vat",
          value: formatNumber(upfTotalVAT,2),
        },
        {
          field: "CUSTOM_CONTRACT_TOTAL",
          id: "ax_contract_total",
          value: formatNumber(upfTotal + recTotal,2),
        },        
        {
          field: "CUSTOM_CONTRACT_TOTAL_VAT",
          id: "ax_contract_total_vat",
          value: formatNumber(upfTotalVAT + recTotalVAT,2),
        },        
        {
          field: "CUSTOM_TERM_LENGTH",
          id: "ax_contract_total_vat",
          value: formatNumber((termLengths.size > 0 ? Math.max(...termLengths) : 0), 0),
        },    
        {
          field: "CUSTOM_REVISIONNUMBER",
          id: "ax_revision_number",
          value: quoteRevision?.revisionNumber.toString() || ""
        },
        {
          field: "CUSTOM_QUOTENUMBER",
          id: "ax_quote_number",
          value: quoteRevision?.quoteId.toString() || ""
        }
      ]
    , [recTotal, annualTotal, upfTotal, quoteRevision]);   

    useEffect(() => {
        if (!templatesData) return;

        // It is possible for the quoteRevision.quoteTemplateId to be the previously selected template id
        // briefly when switching templates, so we need to guard against that case to avoid picking the wrong 
        // template and then having a mismatch between the custom sections and the content which causes
        // the preview to break.  We do this by passing in the selectedTemplate as an argument to this hook
        // and prioritising it over the quoteRevision.quoteTemplateId when determining the template to use for
        // the custom sections.  This way, when the user selects a new template, the custom sections will update
        // immediately based on the selected template, rather than waiting for the quoteRevision to update which
        // can briefly have the old template id.
        const template = selectedTemplate ?? templatesData.find(t => t.id === quoteRevision?.quoteTemplateId);

        if (!template || template.customSections === undefined) return;

        let jsonCustomSections = JSON.parse(template.customSections);

        if (!Array.isArray(jsonCustomSections) || jsonCustomSections.length === 0) return

        const hasAnyTextContent = v => {
            if (!v) return false;
            const d = document.createElement("div");
            d.innerHTML = v;
            d.querySelectorAll("br").forEach(b => b.remove());
            return (d.textContent || "").length > 0;
        };

        if (jsonCustomSections) {
            var sections = [];

            if (Array.isArray(quoteRevision.quoteAdditionalFields)) {
                quoteRevision.quoteAdditionalFields.forEach(qaf => {
                    sections.push({
                        id: qaf.id,
                        field: qaf.field,
                        value: qaf.value
                    })
                })
            }

            calculatedCustomContent.forEach(cc => sections.push(cc))

            jsonCustomSections.forEach(element => {
                let existingEntry = sections.find(c => c.id === element.id);

                // Fallback: the same field name can exist in both  old and new template (when changing
                // templates) but with different UUIDs (each template creates its own id). If the ID 
                // lookup misses, search quoteAdditionalFields by field name so the saved value is not
                // lost.
                if (!existingEntry && Array.isArray(quoteRevision?.quoteAdditionalFields)) {
                    const priorEntry = quoteRevision.quoteAdditionalFields.find(c => c.field === element.field);
                    if (priorEntry && hasAnyTextContent(priorEntry.value)) {
                        // Adopt the saved value under the current template's element id so
                        // downstream consumers see the right id.
                        existingEntry = { id: element.id, field: element.field, value: priorEntry.value };
                        sections.push(existingEntry);
                    }
                }

                const defaultValue = element.defaultValue 
                    ? element.defaultValue 
                    : element.salesforceDefaultValue 
                        ? (opportunityFull?.[element.salesforceDefaultValue.replace("opportunity_", "")] ??
                           accountFull?.[element.salesforceDefaultValue.replace("account_", "")] ?? "")
                        : ""

                const formattedDefaultValue =
                    element.type === "TextArea" &&
                    typeof element.salesforceDefaultValue === "string" &&
                    element.salesforceDefaultValue.length > 0
                        ? "<p>" + defaultValue + "</p>"
                        : (defaultValue ?? "").toString();

                if (!existingEntry) {
                    sections.push({
                        id: element.id,
                        field: element.field,
                        value: formattedDefaultValue
                    });
                } else if (!hasAnyTextContent(existingEntry.value) && defaultValue !== "") {
                    if (sections.some(s => s.id === existingEntry.id)) {
                        existingEntry.value = formattedDefaultValue;
                    } else {
                        sections.push({
                            ...existingEntry,
                            value: formattedDefaultValue
                        })
                    }
                }
            });

            setCustomContent(sections);
        }
    }, [quoteRevision?.quoteAdditionalFields, calculatedCustomContent, quoteRevision?.quoteTemplateId, templatesData, opportunityFull, accountFull, selectedTemplate]);

    useEffect(() => {
        flattenGroupsAndItems("upfront", quoteCustomisation, quoteItems, setUpfrontItems);
        flattenGroupsAndItems("recurring", quoteCustomisation, quoteItems, setRecurringItems);
    }, [quoteCustomisation, quoteItems]);


    useEffect(() => {
        var exportData = {
            upfront: structuredClone(upfrontItems),
            recurring: structuredClone(recurringItems)
        }

        const removeUnnecessaryProperties = (items) => {
            // Remove properties that we don't want available in the export 
            items.forEach(exportItem => {
                delete exportItem.items
                delete exportItem.expanded
                delete exportItem.id
                delete exportItem.isRecurring
                delete exportItem.isUpfront
                delete exportItem.quoteRevision
                delete exportItem.quoteRevisionId

                if (exportItem.rowType === "child") {
                    delete exportItem.name // We don't want to show the name of the group, as it should be on a header line above
                } else if (exportItem.rowType === "group") {
                    if (exportItem.showChildren) {
                        delete exportItem.quantity
                        delete exportItem.upfrontPrice
                        delete exportItem.recurringPrice
                        delete exportItem.annualPrice
                        delete exportItem.totalUpfrontPrice
                        delete exportItem.totalRecurringPrice
                        delete exportItem.totalAnnualPrice                    
                    }
                }
            });
        }

        const formatFieldsForDocument = (items) => {
            items.forEach(exportItem => {
                exportItem.upfrontPrice = formatNumber(exportItem.upfrontPrice)
                exportItem.totalUpfrontPrice = formatNumber(exportItem.totalUpfrontPrice)
                exportItem.recurringPrice = formatNumber(exportItem.recurringPrice)
                exportItem.totalRecurringPrice = formatNumber(exportItem.totalRecurringPrice)
                exportItem.annualPrice = formatNumber(exportItem.annualPrice)
                exportItem.totalAnnualPrice = formatNumber(exportItem.totalAnnualPrice)
                exportItem.totalContractPrice = formatNumber(exportItem.totalContractPrice)

                if (exportItem.rowType === "child") {
                    exportItem.nameOnQuote = exportItem.name
                }
            });
        }

        const removeHiddenItems = (items, groups) => {
            return items.filter(item =>
                item.visible === true &&
                (item.rowType === "group" ||
                    (item.rowType === "child" && groups.find(group =>
                        group.guid === item.groupGuid &&
                        group.showChildren === true &&
                        group.visible === true))
                )
            )
        }

        exportData.upfront = removeHiddenItems(exportData.upfront, quoteCustomisation.groups.upfront)
        exportData.recurring = removeHiddenItems(exportData.recurring, quoteCustomisation.groups.recurring)

        formatFieldsForDocument(exportData.upfront)
        formatFieldsForDocument(exportData.recurring)

        removeUnnecessaryProperties(exportData.upfront);
        removeUnnecessaryProperties(exportData.recurring);

        const collapseGroupsOnToFirstChild = (items) => {
            var collapsedItems = [];

            for (let i = 0; i < items.length; i++) {
                const item = items[i];

                if (item.rowType === "group" && item.showChildren) {
                    var nextItem = items[i + 1]
                    if (nextItem && nextItem.rowType === "child") {
                        // Take first child and put in place of group, but put group name as name
                        collapsedItems.push({
                            ...nextItem,
                            name: item.name // take name from the group
                        });
                        i++; // skip next item as we've already added it
                    } else {
                        // no child found, just add the group as is
                        collapsedItems.push(item);
                    }
                } else {
                    // if it's a child, or a group that's not showing children, then just add it
                    collapsedItems.push(item);
                }
            }

            return collapsedItems;
        }

        exportData.upfront = collapseGroupsOnToFirstChild(exportData.upfront).filter(x => x.quantity !== undefined);
        exportData.recurring = collapseGroupsOnToFirstChild(exportData.recurring).filter(x => x.quantity !== undefined);

        console.log("Export Data:", exportData)

        setExportData(exportData);
    }, [quoteCustomisation, upfrontItems, recurringItems]);

    const flattenGroupsAndItems = (groupName, quoteCustomisation, filteredQuoteItems, setItems) => {
        var updatedItems = []
        const groups = groupName === "upfront" ? quoteCustomisation.groups.upfront : quoteCustomisation.groups.recurring

        groups.forEach(group => {

            var groupItem = {
                ...group,
                id: group.guid,
                name: group.name,
                rowType: "group",
                showChildren: group.showChildren,
                depth: 0,
                expanded: group.expanded,
                visible: group.visible
            }

            updatedItems.push(groupItem)

            group.items.forEach(groupItemRef => {
                // Note that the groupItemRef is just a reference to the full
                // item.  It only has the guid and visibility info

                var quoteItem = filteredQuoteItems.find(x => groupItemRef.lineEditorId 
                                                            ? groupItemRef.lineEditorId === x.lineEditorId // New format Jan 2026, to support multiple items having same guid (adding same module more than once)
                                                            : x.guid === groupItemRef.guid && (group.guid === x.quoteModuleGuid || x.quoteModuleGuid === null) // Old format, that gets confused if there's duplicate guids
                                                        );

                if (!quoteItem && !groupItemRef.lineEditorId) {
                    // Quote item not found.  Since there's no lineEditorId, this is an old-format item under a group (Pre Jan 2026). 
                    // 
                    // This missing item might be a module item that was moved into a different group.

                    // Just look by guid only. If there's two with the same guid we'll just take the first unadded one that we find
                    // any others should get picked up by subsequent iterations
                    quoteItem = filteredQuoteItems.find(x => x.guid === groupItemRef.guid &&
                      updatedItems.some(ui => ui.id === x.lineEditorId) === false // make sure we haven't already added it.
                    );
                }

                if (quoteItem) {
                    var childItem = {
                        ...quoteItem,
                        id: quoteItem.lineEditorId,
                        groupGuid: group.guid,
                        name: quoteItem.internalName,
                        rowType: "child",
                        type: group.type,
                        depth: 1,
                        visible: groupItemRef.visible,
                        groupIsVisible: groupItem.visible
                    }

                    if (groupName === "upfront" && childItem.upfrontNameOnQuote) {
                        childItem.name = childItem.upfrontNameOnQuote
                    }

                    if (groupName === "recurring" && childItem.recurringNameOnQuote) {
                        childItem.name = childItem.recurringNameOnQuote
                    }

                    updatedItems.push(childItem)
                }
            });
        });

        setItems(updatedItems)
    }

    useEffect(() => {
        if (quoteCustomisation.length === 0) return;

        flattenGroupsAndItems("upfront", quoteCustomisation, quoteItems, setUpfrontItems);
    }, [quoteCustomisation?.groups?.upfront, quoteItems])

    useEffect(() => {
        if (quoteCustomisation.length === 0) return;

        flattenGroupsAndItems("recurring", quoteCustomisation, quoteItems, setRecurringItems);
    }, [quoteCustomisation?.groups?.recurring, quoteItems])

    const accounts = useMemo(() => {
        if (!accountId || !allAccounts?.length) return [];
        const key = accountId.toString().trim();
        return allAccounts.filter(a => (a.Id ?? a.id)?.toString().trim() === key);
    }, [allAccounts, accountId]);

    const users = useMemo(() => {
        if (!ownerId || !allUsers?.length) return [];
        const key = ownerId.toString().trim();
        return allUsers.filter(u => (u.Id ?? u.id)?.toString().trim() === key);
    }, [allUsers, ownerId]);

    const salesForceData = { opportunity, accounts, users };

    return {
        salesForceData,
        customContent,
        exportData,
        upfrontItems,
        recurringItems,
        isLoadingOpportunity,
        isLoadingTemplates,
        templatesData,
        templatesError
    };
}